import React, { MouseEvent, useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { InvalidCredentialsBox, ImageSide, TextSide, Wrap, LinksGroup, DividerWithText } from './styled';

import { ReactComponent as BluePinkWarning } from '@assets/icons/blue-pink-warning.svg';
import { H3 } from '@components/Typography';
import PasswordField from '@components/FormElements/FormikFields/Password';
import InputField from '@components/FormElements/FormikFields/Input';
import { addNotificationActionCreator } from '@store/notifications/actions';
import { NotificationType } from '@store/notifications/types';
import { Button } from '@components/Buttons/Button';
import { FieldsGroup } from '@components/FormElements/styled';
import loginUser from '@services/OAuthServices/loginUser';

import { setExpirationDate, setRefreshToken, setToken } from '@utils/localStorage';

import { calculateExpirationTime } from '@utils/date';
import { LOAD_USER } from '@utils/paths';

import history from '@utils/history';
import { setOAuthWithUser } from '@store/auth/actions';
import GoogleSsoBtn from '@components/GoogleSsoBtn';

interface Props {
  handleSignUpClick: (e: MouseEvent) => void;
  handleResetPassClick: (e: MouseEvent) => void;
}

const SignIn = ({ handleSignUpClick, handleResetPassClick }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean | undefined>(false);
  const [ready, setReady] = useState<boolean | undefined>(false);
  const [errorCredentials, setErrorCredentials] = useState<boolean | undefined>(false);
  const [errorActive, setErrorActive] = useState<boolean | undefined>(false);
  const validationSchema = Yup.object().shape({
    username: Yup.string().email(t('forms.signIn.error.emailFormat')).required(t('forms.signIn.error.emailRequired')),
    password: Yup.mixed().required(t('forms.signIn.error.passwordRequired')),
  });

  useEffect(() => {
    if (ready) {
      history.push(LOAD_USER);
    }
  }, [ready]);

  return (
    <>
      <InvalidCredentialsBox visible={errorCredentials || errorActive}>
        <ImageSide>
          <BluePinkWarning />
        </ImageSide>
        <TextSide>
          {errorCredentials && (
            <>
              <p className="title">{t('forms.signIn.error.invalidCredentials.title')}</p>
              <p className="description">{t('forms.signIn.error.invalidCredentials.description')}</p>
            </>
          )}
          {errorActive && <p className="description">{t('forms.signIn.error.inactive.description')}</p>}
        </TextSide>
      </InvalidCredentialsBox>
      <Wrap>
        <Formik
          validateOnChange={false}
          initialValues={{
            username: '',
            password: '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            setErrorCredentials(false);
            setErrorActive(false);
            setLoading(true);
            const { username, password } = values;

            loginUser({ username, password })
              .then((results) => {
                const { access_token, token_type, refresh_token, expires_in } = results;
                if (access_token && token_type) {
                  const tokenUser = `${token_type} ${access_token}`;
                  const expirationDate = calculateExpirationTime(expires_in);
                  const loginPayload = {
                    username,
                    token: tokenUser,
                    isAuthenticated: true,
                    refresh: refresh_token,
                    expires: calculateExpirationTime(expires_in),
                    isLogin: true,
                  };
                  dispatch(setOAuthWithUser(loginPayload));
                  setToken(tokenUser);
                  setRefreshToken(`${refresh_token}`);
                  setExpirationDate(`${expirationDate}`);
                  return setReady(true);
                }
                const { error } = results;
                if (error === 'invalid_credentials') {
                  setLoading(false);
                  return setErrorCredentials(true);
                }
                if (error === 'inactive_user') {
                  setLoading(false);
                  return setErrorActive(true);
                }

                return setLoading(false);
              })
              .catch(() => {
                dispatch(
                  addNotificationActionCreator({
                    type: NotificationType.Error,
                    title: t('forms.signIn.error.network'),
                  })
                );
                return setLoading(false);
              });
          }}
        >
          {({ errors }) => (
            <Form>
              <H3>{t('forms.signIn.caption')}</H3>
              <FieldsGroup>
                <InputField
                  name="username"
                  placeholder={t('forms.signIn.placeholder.username')}
                  fieldProps={{
                    label: `${t('forms.signIn.label.username')}*`,
                    isLoading: loading,
                    error: errors.username,
                  }}
                />
                <PasswordField
                  name="password"
                  fieldProps={{
                    label: `${t('forms.signIn.label.password')}*`,
                    isLoading: loading,
                    error: errors.password,
                  }}
                  placeholder={t('forms.signIn.placeholder.password')}
                />
              </FieldsGroup>
              <Button type="submit" isDisabled={loading}>
                {t('forms.signIn.cta.submit')}
              </Button>
              <>
                <DividerWithText>
                  <span>o</span>
                </DividerWithText>
                <GoogleSsoBtn />
              </>
            </Form>
          )}
        </Formik>
        <LinksGroup>
          <Button $type="link" onClick={handleSignUpClick}>
            {t('forms.signIn.cta.signUp')}
          </Button>
          <Button $type="link" data-test="reset-password" onClick={handleResetPassClick}>
            {t('forms.signIn.cta.resetPass')}
          </Button>
        </LinksGroup>
      </Wrap>
    </>
  );
};

export default SignIn;
